import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/http/apiClient';
import { RoleGuard } from '@/lib/auth/RoleGuard';
import { Spinner } from '@/components/atoms/Spinner';
import { ErrorStateBlock } from '@/components/organisms/ErrorStateBlock';
import { AuthenticatedShell } from '@/features/app/AuthenticatedShell';

export const Route = createFileRoute('/admin/rbac')({ component: RbacMatrixPage });

interface RbacMatrix {
  resources: { resource: string; actions: { action: string; rolesAllowed: string[] }[] }[];
}

function RbacMatrixPage() {
  return (
    <RoleGuard allow={['PlatformAdmin']}>
      <RbacContent />
    </RoleGuard>
  );
}

function RbacContent() {
  const matrix = useQuery({
    queryKey: ['rbac-matrix'],
    queryFn: async () => (await apiClient.get<RbacMatrix>('/api/v1/admin/rbac/matrix')).data,
  });

  if (matrix.isLoading) {
    return (
      <AuthenticatedShell activeNavId="admin-rbac">
        <div className="p-8"><Spinner label="Loading matrix" /></div>
      </AuthenticatedShell>
    );
  }
  if (matrix.error || !matrix.data) {
    return (
      <AuthenticatedShell activeNavId="admin-rbac">
        <ErrorStateBlock title="Could not load RBAC matrix" />
      </AuthenticatedShell>
    );
  }

  return (
    <AuthenticatedShell activeNavId="admin-rbac">
      <div className="mx-auto max-w-5xl px-8 py-10">
        <h1 className="font-display text-display tracking-tight text-foreground">
          RBAC <span className="italic text-primary">matrix</span>.
        </h1>
        <p className="mt-3 text-body-sm text-muted-foreground max-w-prose">
          Resource × role permission matrix. PlatformAdmin only.
        </p>
        <div className="mt-8 space-y-6">
          {matrix.data.resources.map((r) => (
            <section key={r.resource} className="rounded-lg border border-border bg-card p-5">
              <h2 className="font-display text-title-sm">{r.resource}</h2>
              <table className="mt-3 w-full text-body-sm">
                <thead>
                  <tr className="border-b border-border-strong">
                    <th className="text-left py-2 text-eyebrow font-mono uppercase tracking-eyebrow text-muted-foreground">Action</th>
                    <th className="text-left py-2 text-eyebrow font-mono uppercase tracking-eyebrow text-muted-foreground">Roles allowed</th>
                  </tr>
                </thead>
                <tbody>
                  {r.actions.map((a) => (
                    <tr key={a.action} className="border-b border-border last:border-b-0">
                      <td className="py-2 font-medium">{a.action}</td>
                      <td className="py-2 font-mono text-caption text-muted-foreground">{a.rolesAllowed.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </AuthenticatedShell>
  );
}
